import { useMemo } from "react";
import { AreaChart, Area, Tooltip, ResponsiveContainer } from "recharts";
import {
  ChartGrid,
  ChartXAxis,
  ChartYAxis,
  CHART_TOOLTIP_STYLE,
} from "../lens/chart-primitives";
import { formatUsd, formatCompact } from "../../format";
import { cn } from "../../utils";

export interface FleetSpendSeries {
  /** Data key on each point, e.g. `cost_shipper`. */
  key: string;
  /** Human label, e.g. `shipper`. */
  label: string;
  /** CSS color (a `var(--chart-N)`). */
  color: string;
}

export interface FleetSpendPoint {
  date: string;
  runs: number;
  [series: string]: number | string;
}

interface FleetSpendChartProps {
  points: FleetSpendPoint[];
  /** Per-role cost series, ordered heaviest-first (stack + legend order). */
  series: FleetSpendSeries[];
  height?: number;
  className?: string;
}

/** Compact money for a chart axis — `$` + the canonical magnitude formatter. */
function compactUsd(v: number): string {
  return `$${formatCompact(v)}`;
}

function shortDate(iso: string): string {
  // 'YYYY-MM-DD' → 'M/D'
  const [, m, d] = iso.split("-");
  return m && d ? `${Number(m)}/${Number(d)}` : iso;
}

/**
 * Fleet spend over time — stacked area of per-role cost ($/day), with an
 * optional runs/day line overlay. The spine chart of the Agents ▸ Fleet
 * Analytics surface: it makes the agent company's daily burn legible and shows
 * which role (shipper / verifier / reviewer) drives it.
 *
 * Presentational only — the page computes the `cost_<role>` series + colors
 * (via `chartColor`) and hands them in. Stack/legend lead with the heaviest
 * role so the dominant cost reads first.
 */
export function FleetSpendChart({
  points,
  series,
  height = 220,
  className,
}: FleetSpendChartProps) {
  const labelByKey = useMemo(
    () => Object.fromEntries(series.map((s) => [s.key, s.label])),
    [series],
  );

  if (points.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center type-body text-muted-foreground",
          className,
        )}
        style={{ height }}
      >
        No fleet runs in this range yet.
      </div>
    );
  }

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height} minWidth={0}>
        <AreaChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <ChartGrid />
          <ChartXAxis
            dataKey="date"
            tickFormatter={shortDate}
            interval="preserveStartEnd"
            minTickGap={42}
          />
          <ChartYAxis tickFormatter={compactUsd} width={52} />
          <Tooltip
            contentStyle={CHART_TOOLTIP_STYLE}
            labelFormatter={(l) => String(l)}
            formatter={(val, name) => {
              const n = typeof name === "string" ? name : String(name);
              return [
                formatUsd(typeof val === "number" ? val : 0),
                labelByKey[n] ?? n,
              ];
            }}
          />
          {series.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stackId="cost"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.2}
              strokeWidth={1.5}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      {/* Legend — heaviest role first, mirrors the stack order. */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 type-subtext">
            <span
              className="size-2 rounded-xs"
              style={{ backgroundColor: s.color }}
              aria-hidden
            />
            <span className="text-muted-foreground">{s.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
